import React from 'react';
import isEmpty from 'lodash/isEmpty';
import type {
  ExternalReactRenderers,
  ExtensionsConfigOptions,
} from '../Editor/types';

// https:/ / tiptap.dev / guide / output;
/**
 * There are two types of things which render.
 * Node - text, paragraph, mention etc. - https://tiptap.dev/api/nodes
 * Marks - wraps around the node, like bold, italic, link etc. - https://tiptap.dev/api/marks
 * "bold" | "code" | "italic" | "link" | "strike" | "underline" | "textStyle"
 *
 * Nodes can be nested within each other. Any node can have multiple marks associated with it.
 * A mark doesn't have a standalone existence. It is always associated with a node.
 */

type NodeTypes = keyof TipTapNodeHandlers;
type MarkTypes = keyof TipTapMarkHandlers;

export interface TipTapBaseNode {
  type: NodeTypes;
  attrs?: Record<string, any>;
  marks?: TipTapBaseMark[];
  content?: TipTapBaseNode[];
  externalRenderers: ExternalReactRenderers;
  extensionConfig: ExtensionsConfigOptions;
  containerWidth: number;
  presetId: string;
  readonly [attr: string]: any;
}

export interface TipTapBaseMark {
  type: MarkTypes;
  attrs?: Record<string, any>;
}

export type TipTapNodeHandler<T extends TipTapBaseNode = TipTapBaseNode> =
  (props: { children?: React.ReactNode; node: T }) => React.JSX.Element;

export type TipTapMarkHandler<T extends TipTapBaseMark = TipTapBaseMark> =
  (props: { children?: React.ReactNode; node: T }) => React.JSX.Element;

export interface TipTapNodeHandlers {
  text: TipTapNodeHandler<TextNode>;
  doc: TipTapNodeHandler;
  paragraph: TipTapNodeHandler;
  heading: TipTapNodeHandler<HeadingNode>;
  hardBreak: TipTapNodeHandler;
  hardBreakTrailing: TipTapNodeHandler;
  emptyParagraph: TipTapNodeHandler;
  bulletList: TipTapNodeHandler;
  listItem: TipTapNodeHandler;
  orderedList: TipTapNodeHandler;
  horizontalRule: TipTapNodeHandler;
  codeBlock: TipTapNodeHandler<CodeBlockNode>;
  blockquote: TipTapNodeHandler;
}

export interface TipTapMarkHandlers {
  link: TipTapMarkHandler<LinkMark>;
  bold: TipTapMarkHandler;
  code: TipTapMarkHandler;
  italic: TipTapMarkHandler;
  strike: TipTapMarkHandler;
  underline: TipTapMarkHandler;
}

// this is the interface the renderer needs to implement
export type TipTapRenderHandlers = TipTapNodeHandlers & TipTapMarkHandlers;

export interface TextNode extends TipTapBaseNode {
  type: 'text';
  text: string;
  content?: undefined;
}

export interface HeadingNode extends TipTapBaseNode {
  type: 'heading';
  attrs: {
    level: '1' | '2' | '3';
  };
}

export interface CodeBlockNode extends TipTapBaseNode {
  type: 'codeBlock';
  attrs: {
    language: string;
  };
}

export interface LinkMark extends TipTapBaseMark {
  type: 'link';
  attrs: {
    href: string;
  };
}

/**
 * Render a tip tap JSON node and all its children
 * @param { TipTapBaseNode} node JSON node to render
 * @param { TipTapNodeHandlers} handlers a handler for each node type
 * @returns tree of components as react elements
 */
export function TipTapRender(props: {
  node: TipTapBaseNode;
  handlers: TipTapRenderHandlers;
  externalRenderers: ExternalReactRenderers;
  extensionConfig: ExtensionsConfigOptions;
  containerWidth: number;
  presetId: string;
}): React.JSX.Element {
  const { node, handlers: mapping } = props;
  node.externalRenderers = props.externalRenderers;
  node.extensionConfig = props.extensionConfig;
  node.containerWidth = props.containerWidth;
  node.presetId = props.presetId;

  // recursively render child content
  const children: React.JSX.Element[] = [];

  // Find link marks, they need special handling because
  // anchors are not simple attributes on an <a> element
  // they are infact wrapping elements that have rules.
  // This deal with other marks breaking the link marks
  // into separate marks and ending up with say three links
  // instead of one when you have <a>a<strong>b</strong>c<a/>
  // without this that would end up as <a>a</a><a>b</a><a>c</a>
  //
  // Logic here is to find the links and then rewrite the content
  // so we have a nested link structure, this works as anchors cannot
  // appear inside each other meaning we can convert:
  // [1]
  // [2]
  // [3]
  // [4]
  // Into:
  // [1]
  //   [2][3]
  // [4]
  // Where 1 becomes the linkwrapper and all the parts with the same
  // href get pushed into its children.
  const anchorsByHref: Record<
    string,
    {
      link?: TipTapBaseMark;
      index?: number;
      startIndex?: number;
      children: any;
    }
  > = {}; // map of href
  let startIndex = 0; // tracks the start of each link, deals with dupes
  const indexToHref: (string | false)[] = []; // used to track which children have been replaced later on
  const indexToHrefDone: string[] = []; // used to track which hrefs have been removed from the above index
  let currentHref;
  node.content &&
    node.content.forEach((child, index) => {
      if (!child.marks?.find) {
        // when we don't have a link, bumped the startIndex as the links
        // span over the text has finished and if there is another of the
        // same href we can handle that uniquely
        startIndex++;
        return;
      }
      const link = child.marks?.find(m => m.type === 'link');
      if (link && link.attrs) {
        // If the link changes increment so we change the id
        if (currentHref && currentHref !== link.attrs.href) {
          startIndex++;
        }
        currentHref = link.attrs.href;
        // Make a uniqueID for the link, handling links with same href in the same block
        const hrefId = `${link.attrs.href}:${startIndex}`;
        if (!anchorsByHref[hrefId]) {
          anchorsByHref[hrefId] = { children: [] };
        }
        // Filters out the link marks as we are hoisting it up a level
        child.marks = child.marks?.filter(m => m.type !== 'link');
        anchorsByHref[hrefId].link = link;
        anchorsByHref[hrefId].children.push(child); // we collect the children
        anchorsByHref[hrefId].index = index;
        anchorsByHref[hrefId].startIndex =
          anchorsByHref[hrefId].startIndex ?? index;
        indexToHref[index] = hrefId;
      } else {
        // when we don't have a link, bumped the startIndex as the links
        // span over the text has finished and if there is another of the
        // same href we can handle that uniquely
        startIndex++;
      }
    });
  if (!isEmpty(anchorsByHref) && node.content) {
    // Here we rewrite the node.content so it include link wrapper with nested
    // children rather than a flat array.
    // We want to maintain the index order here, so we are pushing non links
    // etc so we don't end up with just the links and no other content.
    const newContent: any[] = [];
    node.content.forEach((child, index) => {
      // Not a link
      if (indexToHref[index] === undefined) {
        newContent.push(child);
        return;
      }
      // Ignore hrefs we have already wrapped the children are already added
      // this skips nodes that we have pushed into the link group above in anchorsByHref
      // like: ['href1', 'href1', 'href1'] -> [false, 'href1', 'href1']  -> [false, false, 'href1']
      // We also track the removed items and check that the links are the same, this fixes losing
      // items when you have links stacked together
      if (
        index > 0 &&
        indexToHref[index - 1] === false &&
        indexToHrefDone[index - 1] === indexToHref[index]
      ) {
        indexToHref[index] = false;
        return;
      }
      // Adds special wrapper than renders nothing but nested the correct children
      // and tells the mark to render a link.
      if (indexToHref[index]) {
        const grouped = anchorsByHref[indexToHref[index] as any];
        const linkSpan = {
          type: 'link-wrapper',
          marks: [grouped.link],
          content: grouped.children,
        };
        newContent.push(linkSpan);
        indexToHrefDone[index] = indexToHref[index] as string;
        indexToHref[index] = false;
      }
    });
    node.content = newContent;
  }

  // In the editor view tiptap renders trailing hardbreaks, and without them
  // in the viewer it gets confusing if the user is using shift+enter to break
  // lines. So if we have hardbreak as the last item in content we switch its type.
  // see https://github.com/ProseMirror/prosemirror/issues/1241#issuecomment-1035193462
  if (
    node.content &&
    node.content.length > 0 &&
    node.content[node.content.length - 1]?.type === 'hardBreak'
  ) {
    node.content[node.content.length - 1].type = 'hardBreakTrailing';
  }

  node.content &&
    node.content.forEach((child, ix) => {
      children.push(
        React.createElement(TipTapRender, {
          node: child,
          handlers: mapping,
          key: `${child.type}-${ix}`,
          externalRenderers: props.externalRenderers,
          extensionConfig: props.extensionConfig,
          containerWidth: props.containerWidth,
          presetId: props.presetId,
        })
      );
    });

  const nodeType = node.type;
  // return empty if we are missing a handler for this type
  if (!(nodeType in props.handlers)) {
    // Temp ones never render
    if (['da-muro', 'da-dream', 'spinner'].includes(nodeType)) {
      return React.createElement(React.Fragment, null);
    }
    // eslint-disable-next-line no-console
    console.info(`tiptap renderer, missing node type`, nodeType, node);
    return React.createElement(React.Fragment, null);
  }

  let renderedElement: React.JSX.Element | null = null;
  if (nodeType === 'text') {
    renderedElement = React.createElement(
      mapping[nodeType],
      { node: node as TextNode },
      children
    );
  } else if (nodeType === 'heading') {
    renderedElement = React.createElement(
      mapping[nodeType],
      { node: node as HeadingNode },
      children
    );
  } else if (nodeType === 'codeBlock') {
    renderedElement = React.createElement(
      mapping[nodeType],
      { node: node as CodeBlockNode },
      children
    );
  } else if (nodeType === 'paragraph' && !node.content) {
    // special case for empty paragraphs, make it hard break
    renderedElement = React.createElement(
      mapping['emptyParagraph'],
      { node: node },
      children
    );
  } else {
    renderedElement = React.createElement(
      mapping[nodeType],
      { node: node },
      children
    );
  }

  // now wrap all the marks
  if (node.marks && node.marks.forEach) {
    node.marks.forEach(mark => {
      const markType = mark.type;
      if (markType in props.handlers) {
        switch (markType) {
          case 'link':
            renderedElement = React.createElement(
              mapping[markType],
              {
                node: mark as LinkMark,
              },
              renderedElement
            );
            break;
          default:
            renderedElement = React.createElement(
              mapping[markType],
              {
                node: mark,
              },
              renderedElement
            );
            break;
        }
      } else {
        // eslint-disable-next-line no-console
        console.info(`tiptap renderer, missing mark type`, markType, mark);
      }
    });
  }
  return renderedElement;
}
